import queryString from "query-string";
import base64 from "base-64";
import { getFullName } from "./helper";

/**
 * Extract invitation token from URL query parameters
 * @param {Object} location - React Router location object
 * @returns {Object} - { invitationKey: string|null, invitationEmail: string|null }
 */
export function extractInvitationFromURL(location) {
  if (!location || !location.search) {
    return { invitationKey: null, invitationEmail: null };
  }

  const queryParsed = queryString.parse(location.search);

  if (!queryParsed.key) {
    return { invitationKey: null, invitationEmail: null };
  }

  try {
    const key = queryParsed.key;
    const payload = JSON.parse(base64.decode(key.split(".")[1]));

    return {
      invitationKey: key,
      invitationEmail: payload?.email || null,
      invitationOrganization: payload?.organization || null,
    };
  } catch (error) {
    return { invitationKey: null, invitationEmail: null };
  }
}

/**
 * Store social authentication tokens and user data in localStorage
 * Follows the same pattern as regular login (Login.jsx lines 176-213)
 * @param {Object} authData - Authentication data from backend
 */
export function storeSocialAuthTokens(authData) {
  if (!authData) {
    return;
  }

  // Store JWT tokens
  if (authData.token) {
    localStorage.setItem("access_token", authData.token);
  }

  if (authData.refresh) {
    localStorage.setItem("refresh_token", authData.refresh);
  }

  // Store user data
  if (authData.user_id) {
    localStorage.setItem("user_id", authData.user_id);
  }

  if (authData.email) {
    localStorage.setItem("email", authData.email);
  }

  // Store user profile data
  if (authData.user_profile) {
    localStorage.setItem("user_profile", authData.user_profile.id);
    localStorage.setItem("user_fullName", getFullName(authData.user_profile));

    if (authData.user_profile.color) {
      localStorage.setItem("user_profile_color", authData.user_profile.color);
    }

    if (authData.user_profile.timezone && authData.user_profile.timezone.tz) {
      localStorage.setItem(
        "user_timezone",
        JSON.stringify(authData.user_profile.timezone)
      );
    }
  }

  // Store created_at timestamp
  if (authData.created_at) {
    localStorage.setItem("created_at", authData.created_at);
  }

  // Handle first login flag
  if (authData.is_first_login) {
    localStorage.setItem("is_first_login", "true");
  }

  // Check for owner role in organizations
  if (authData.organizations && Array.isArray(authData.organizations)) {
    authData.organizations.forEach((organization) => {
      if (organization.role === "owner" && organization.is_active === true) {
        localStorage.setItem("is_owner", "true");
      }
    });
  }
}

/**
 * Clear all authentication data from localStorage
 */
export function clearAuthTokens() {
  const keysToRemove = [
    "access_token",
    "refresh_token",
    "user_id",
    "email",
    "user_profile",
    "user_fullName",
    "user_profile_color",
    "user_timezone",
    "created_at",
    "is_first_login",
    "is_owner",
  ];

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/**
 * Determine the appropriate redirect path after successful auth
 * @param {Object} authData - Authentication data from backend
 * @param {Object} redirectLocation - Query params with redirect path
 * @returns {string} - Path to redirect to
 */
export function getAuthRedirectPath(authData, redirectLocation) {
  // If there's a specific redirect in query params, use it
  if (redirectLocation?.redirect) {
    return `/user/${redirectLocation.redirect}`;
  }

  // If profile is not created, redirect to profile creation
  if (authData.user_profile === "undefined" || !authData.is_profile_created) {
    return "/user/user-profile";
  }

  return "/user/dashboard";
}

/**
 * Get user-friendly error message for social auth errors
 * @param {Object} error - Error object from API
 * @returns {string} - User-friendly error message
 */
export function getSocialAuthErrorMessage(error) {
  if (!error) {
    return "An unknown error occurred";
  }

  // Check for specific error responses
  if (error.response?.data) {
    const errorData = error.response.data;

    if (errorData.error) {
      return errorData.error;
    }

    if (errorData.detail) {
      return errorData.detail;
    }

    if (
      errorData.non_field_errors &&
      Array.isArray(errorData.non_field_errors)
    ) {
      return errorData.non_field_errors[0];
    }

    if (errorData.message) {
      return errorData.message;
    }
  }

  // Check for error message directly
  if (error.message) {
    return error.message;
  }

  // Network errors
  if (!error.response) {
    return "Network error. Please check your connection and try again.";
  }

  // Default error message
  return "Authentication failed. Please try again.";
}
